import { useQuery } from "@tanstack/react-query";

import api from "@/app/lib/api/api";
import useDebouncedValue from "@/app/lib/hooks/useDebouncedValue";
import type { FindCitiesOutputDto } from "@/app/lib/types/codegen";
import type { City } from "@/app/lib/types/types";
import { toTitleCase } from "@/app/lib/utils/titleCase";

export function useHotelsCitiesSearch(searchQuery: string) {
  const debouncedSearch = useDebouncedValue(searchQuery.trim(), 500);

  return useQuery({
    queryKey: ["hotels-cities-search", debouncedSearch] as const,
    queryFn: ({ signal }) =>
      api.get<FindCitiesOutputDto>(`/mappings/cities`, {
        params: { searchQuery: debouncedSearch },
        signal,
      }),
    select: (response): City[] => {
      const { cities } = response.data.data;

      return cities.map((city) => ({
        ...city,
        // IRIX returns city names in uppercase, so we need to convert them to title case
        name: toTitleCase(city.name),
        type: "CITY", // this is only needed for type errors, this value already has "CITY" type but generated types are set to 'string'
      }));
    },
    enabled: debouncedSearch.length > 2,
    staleTime: 1000 * 60 * 60 * 24, // fetch fresh translations after 24 hours
  });
}
